import type { DeviceDto, DeviceType, SessionDto, StopSessionInput } from '@gc/shared';

import { api, ApiError } from '@/lib/api';
import { buildStartSessionPayload } from './sessionLogic';
import { STOP_ROUNDS_DEFAULT, stopRoundsPayload, type StopRoundsEntry } from './StopRoundsFields';

/**
 * Bulk actions over the board's checkbox selection (wave 3 #12, #6-UI).
 *
 * Every runner here follows the screens push convention: devices are visited
 * IN TURN and a failure never aborts the rest — the point of the outcome list
 * is that staff see every machine's answer, and one offline PC hiding the
 * other nineteen would be worse than no bulk action at all. Sequential on
 * purpose, too: each call may reach out to a live machine over the venue's one
 * server, and a stampede of twenty is self-inflicted load.
 *
 * The power / support-unlock / pause / resume routes are agent C's half of the
 * wave (shapes frozen in `@gc/shared`: `devicePowerSchema`,
 * `pauseSessionSchema`, `resumeSessionSchema`); until C lands, they answer
 * 404 and the outcome list reports that honestly per device.
 */

/**
 * "It worked — and here is what it cost."
 *
 * Pause is the opposite of Play: it closes the machine back to its login
 * screen, and for a machine Play opened there is nothing to settle. Aim it at a
 * PAYING sitting, though, and it still ends that sitting — the time played goes
 * onto the customer's tab as money owed, exactly as "Stop & close" would.
 *
 * That is a legitimate thing to do and it is not a failure, so it must not be
 * reported as one. It must also not be silent: a button called Pause moving
 * money is the kind of surprise staff discover at the till, an hour later, in
 * front of the customer.
 */
export type BulkWarningCode = 'paid_session_ended';

export interface BulkOutcome {
  deviceId: string;
  deviceName: string;
  ok: boolean;
  /** Refusal code when the API refused; `no_session` is minted client-side. */
  code: string | null;
  message: string | null;
  /**
   * Set only on a SUCCEEDED outcome whose side-effect half did not land.
   * Absent everywhere else — a runner with nothing to warn about does not
   * carry the key at all.
   */
  warning?: BulkWarningCode | null;
}

export interface BulkDeviceRef {
  id: string;
  name: string;
}

/** The three tones the outcome list renders. Success and warning are BOTH ok. */
export type BulkOutcomeTone = 'ok' | 'warning' | 'failed';

export function bulkOutcomeTone(outcome: BulkOutcome): BulkOutcomeTone {
  if (!outcome.ok) return 'failed';
  return outcome.warning ? 'warning' : 'ok';
}

/** i18n key per warning, so the dialog never has to know the code shapes. */
export const BULK_WARNING_KEYS: Record<BulkWarningCode, string> = {
  paid_session_ended: 'deviceBoard.bulk.warning.paidSessionEnded',
};

/**
 * Did closing this machine move money?
 *
 * Read off the closed sitting itself rather than guessed from the device: a
 * free-mode sitting settles at zero however long it ran, and a paying one is
 * only known to have cost something once the server has billed it.
 */
export function endedPayingSession(closed: { totalMd?: string } | null | undefined): boolean {
  return Boolean(closed?.totalMd) && closed!.totalMd !== '0';
}

/** The failure row every runner reports, from whatever was thrown. */
function toFailure(device: BulkDeviceRef, caught: unknown): BulkOutcome {
  return {
    deviceId: device.id,
    deviceName: device.name,
    ok: false,
    code: caught instanceof ApiError ? caught.code : null,
    message: caught instanceof Error ? caught.message : null,
  };
}

// Generic over the device shape so a runner that needs more than id and name —
// `bulkStartSession` reads each machine's own free-mode flag — does not have to
// look its own argument back up in the list it was handed.
export async function runPerDevice<T extends BulkDeviceRef>(
  devices: readonly T[],
  run: (device: T) => Promise<unknown>,
): Promise<BulkOutcome[]> {
  const outcomes: BulkOutcome[] = [];
  for (const device of devices) {
    try {
      await run(device);
      outcomes.push({
        deviceId: device.id,
        deviceName: device.name,
        ok: true,
        code: null,
        message: null,
      });
    } catch (caught) {
      outcomes.push(toFailure(device, caught));
    }
  }
  return outcomes;
}

/**
 * **Play** — let somebody use this machine, free, with no account.
 *
 * The owner's definition, and the whole of it (restated 2026-08-12: "the Play
 * function is only used when someone sits and a staff member opens the PC for
 * them for free play, so they can play for as long as they want with no
 * charge"). The machine opens, the customer plays, and no money is involved
 * at any point: `complimentary: true` makes the server snapshot a ZERO rate,
 * so ending it later charges nothing whatever the clock says, and the
 * Outstanding screen never lists the party at all.
 *
 * `freeMode: true` rides along so a PC also hands over the Windows desktop,
 * exactly as a paid free-mode sitting would — the two flags stay separate
 * because free MODE alone is about the screen and bills normally. Only a PC
 * HAS a desktop (2026-09-23): a PlayStation opened with Play is a plain
 * complimentary sitting whose television switches to the console, and asking
 * for free mode there only fired a hand-over at a seat with no agent. The
 * server now drops it on a non-PC anyway; this keeps the wire honest.
 *
 * The button needs `discounts.apply`: giving the room away is a privilege,
 * and the server gates `complimentary` on it. Better a button an unprivileged
 * employee never sees than one that 403s when they press it.
 *
 * Open-ended on purpose — free play has no meter to run out.
 */
export function bulkPlay(
  devices: readonly (BulkDeviceRef & { deviceType?: DeviceType })[],
): Promise<BulkOutcome[]> {
  return runPerDevice(devices, (device) =>
    api.post(
      '/sessions',
      buildStartSessionPayload({
        deviceId: device.id,
        identity: { kind: 'walkIn', name: device.name },
        fallbackName: device.name,
        durationMinutes: '',
        freeMode: device.deviceType === undefined || device.deviceType === 'PC',
        complimentary: true,
      }),
    ),
  );
}

/**
 * **Pause** — the exact opposite of {@link bulkPlay}: back to the login screen.
 *
 * Ends the sitting, which is what returns the portal to sign-in (the page
 * notices within one poll). For a machine opened with Play there is nothing to
 * settle — the sitting was free, so it closes at zero.
 *
 * **It is not the billing clock**, despite the name. If staff aim it at a
 * PAYING customer it still ends that sitting and the time played lands on their
 * tab exactly as "Stop & close" would; the confirm says so before anyone
 * commits, because a button labelled Pause must not move money silently.
 *
 * **A paying round-priced sitting is billed on its rounds** (OW19 review): a
 * console or a cinema sold by the match is priced on rounds alone, and the
 * stop schema reads `{}` as zero rounds, so Pause used to close every one
 * owing nothing for play. `rounds` is what the confirm asked, by session id
 * (`pauseCountsRounds` says which); an unanswered line is the fields' own
 * default, one single round — never none. The stop body is the Device List
 * End's own (`EndReservationDialog`): the device's reservation type, 3 ball
 * only where it has a 3-ball price.
 */
export async function bulkPause(
  devices: readonly DeviceDto[],
  rounds: ReadonlyMap<string, StopRoundsEntry> = new Map(),
): Promise<BulkOutcome[]> {
  const outcomes: BulkOutcome[] = [];

  for (const device of devices) {
    const session = device.currentSession;
    if (!session) {
      // Nothing running is not worth a network round-trip, but silently
      // skipping it would make the summary lie about what was asked for.
      outcomes.push({
        deviceId: device.id,
        deviceName: device.name,
        ok: false,
        code: 'no_session',
        message: null,
      });
      continue;
    }

    try {
      // `{}` and never `undefined`: a bodyless POST carries no Content-Type,
      // Express 5 leaves req.body undefined, and the strict schema 400s. That
      // is what made every bulk action of this shape fail once already (R4#1).
      const body: StopSessionInput = pauseCountsRounds(device)
        ? stopRoundsPayload(
            rounds.get(session.id) ?? STOP_ROUNDS_DEFAULT,
            device.threeBallPriceMd != null,
          )
        : {};
      const closed = await api.post<SessionDto>(`/sessions/${session.id}/stop`, body);
      outcomes.push({
        deviceId: device.id,
        deviceName: device.name,
        ok: true,
        code: null,
        message: null,
        warning: endedPayingSession(closed) ? 'paid_session_ended' : null,
      });
    } catch (caught) {
      outcomes.push(toFailure(device, caught));
    }
  }

  return outcomes;
}

/**
 * Must Pause count this device's rounds? Only a paying sitting on a
 * round-priced device. Play's own sitting (`complimentary`) snapshots a zero
 * rate, so its rounds would bill nothing; asking for them is noise on the one
 * close Pause exists for.
 */
export function pauseCountsRounds(
  device: Pick<DeviceDto, 'reservationType' | 'currentSession'>,
): boolean {
  return (
    device.reservationType === 'ROUND' &&
    device.currentSession !== null &&
    !device.currentSession.complimentary
  );
}

/** `POST /devices/:id/power` under `devices.manage` (agent C's route). */
export function bulkPower(
  devices: readonly BulkDeviceRef[],
  action: 'shutdown' | 'restart',
): Promise<BulkOutcome[]> {
  return runPerDevice(devices, (device) => api.post(`/devices/${device.id}/power`, { action }));
}

/** `POST /devices/:id/support-unlock` under `hotkey.unlock` (agent C's route). */
export function bulkSupportUnlock(devices: readonly BulkDeviceRef[]): Promise<BulkOutcome[]> {
  return runPerDevice(devices, (device) => api.post(`/devices/${device.id}/support-unlock`));
}

// "Play Ad" used to live here as a per-selection assign+push macro; it now
// targets every FREE TV seat server-side (`POST /screens/ad`) — see
// PlayAdDialog. The per-device assign/push pair remains available on the
// Screens page itself.
